"use client";

import React, { useEffect, useRef, useState } from "react";
import { Button } from "@/components/ui/button";
import { Link } from "@/components/common/Link";
type UseRevealOnScrollOptions = {
  once?: boolean;
  threshold?: number | number[];
  rootMargin?: string;
};
function useRevealOnScroll<T extends HTMLElement = HTMLElement>({
  once = true,
  threshold = 0.2,
  rootMargin = "0px"
}: UseRevealOnScrollOptions = {}) {
  const ref = useRef<T | null>(null);
  const [isVisible, setIsVisible] = useState(false);
  useEffect(() => {
    const node = ref.current;
    if (!node) {
      return;
    }
    if (typeof window === "undefined" || typeof IntersectionObserver === "undefined") {
      setIsVisible(true);
      return;
    }
    const observer = new IntersectionObserver(entries => {
      const entry = entries[0];
      if (entry?.isIntersecting) {
        setIsVisible(true);
        if (once) {
          observer.unobserve(entry.target);
        }
      } else if (!once) {
        setIsVisible(false);
      }
    }, {
      threshold,
      rootMargin
    });
    observer.observe(node);
    return () => {
      observer.disconnect();
    };
  }, [once, rootMargin, threshold]);
  return {
    ref,
    isVisible
  };
}
export default function BehindTheCode() {
  const {
    ref: imageRef,
    isVisible: isImageVisible
  } = useRevealOnScroll<HTMLDivElement>({
    threshold: 0.1
  });
  const {
    ref: textRef,
    isVisible: isTextVisible
  } = useRevealOnScroll<HTMLDivElement>({
    threshold: 0.1
  });
  return <section data-section-id="131896" id="ekspert-od-twojego-ciepła" className="bg-background text-foreground py-24 lg:py-32 overflow-hidden relative">
      {}
      <div className="absolute top-1/2 left-0 -translate-y-1/2 w-[800px] h-[800px] bg-primary/5 rounded-full blur-[120px] pointer-events-none" />

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-20 items-center">
          
          {}
          <div ref={imageRef} className={`lg:col-span-7 relative transition-all duration-1000 ease-out ${isImageVisible ? "opacity-100 translate-x-0" : "opacity-0 -translate-x-12"}`}>
            <div className="relative aspect-[4/5] md:aspect-[16/10] lg:aspect-[4/5] overflow-hidden rounded-t-[200px] rounded-bl-[200px] rounded-br-[40px] bg-muted border-8 border-muted shadow-2xl">
              <img src="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1606250/img/clean_modern_radiator.png" alt="A clean and efficient modern home radiator" className="w-full h-full object-cover" />
              {}
              <div className="absolute inset-0 bg-primary/10 mix-blend-multiply pointer-events-none" />
              <div className="absolute inset-0 bg-gradient-to-t from-secondary/40 via-transparent to-transparent pointer-events-none" />
              
              {}
              <div className="absolute top-8 left-8 w-16 h-16 border-t-4 border-l-4 border-primary/30 opacity-40" />
              <div className="absolute bottom-8 right-8 w-16 h-16 border-b-4 border-r-4 border-primary/30 opacity-40" />
            </div>
          </div>

          {}
          <div ref={textRef} className={`lg:col-span-5 flex flex-col justify-center space-y-8 transition-all duration-1000 delay-200 ease-out ${isTextVisible ? "opacity-100 translate-x-0" : "opacity-0 translate-x-12"}`}>
            <div className="space-y-4">
              <h2 className="text-sm font-mono tracking-widest text-primary uppercase font-semibold">
                O nas i naszej misji
              </h2>
              <h3 className="text-3xl md:text-4xl lg:text-5xl font-sans font-extrabold tracking-tighter text-secondary leading-tight">
                Czysta instalacja, <br />
                <span className="text-primary">niższe rachunki.</span>
              </h3>
            </div>

            <div className="space-y-6 text-lg text-muted-foreground font-sans font-medium leading-relaxed">
              <p>Nazywam się Piotr, założyciel Hydroflush. Od lat dbam o to, by instalacje grzewcze w domach działały z maksymalną sprawnością. Moje doświadczenie to gwarancja, że Twoje <strong className="text-secondary font-bold" data-ast-id="f:/src/pages/Home/sections/BehindTheCode.tsx|sig:l7rv4y" data-ast-start="4286" data-ast-end="4368" zn_id="81">ogrzewanie odzyska pełną moc</strong>, a dom stanie się bardziej ekologiczny.</p>
              
              <p>
                W Hydroflush łączymy pasję do techniki z dbałością o każdy detal. Wykorzystujemy specjalistyczny sprzęt do <strong className="text-primary font-bold">płukania chemicznego i odmulania</strong>, usuwając zatory, które kradną Twoje ciepło i pieniądze. Każdy system traktujemy z precyzją, na jaką zasługuje Twój dom.
              </p>

              <p className="italic text-secondary font-semibold">
                Zaufaj lokalnemu ekspertowi – przywrócimy przepływ energii w Twojej instalacji CO szybko, czysto i profesjonalnie.
                <br />
                <span className="block mt-4 font-mono text-sm not-italic opacity-70">— Piotr, właściciel Hydroflush</span>
              </p>
            </div>

            <div className="pt-4">
              <Button asChild size="lg" variant="outline" className="rounded-xl px-10 border-primary/50 text-primary hover:bg-primary hover:text-white transition-all duration-300 shadow-md hover:shadow-lg">
                <Link to="/AboutUsPage">
                  Poznaj naszą ofertę
                </Link>
              </Button>
            </div>
          </div>

        </div>
      </div>
    </section>;
}